import "./Navigator.scss";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Breadcrumb, Button, Layout, theme} from "antd";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import LocaleSwitch from "@components/LocaleSwitch";
import CurrencySwitch from "@components/CurrencySwitch";
import UserMenu from "@components/userMenu/UserMenu";
import SystemMessages from "@components/navbar/SystemMessages";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {GlobalContext} from "@tools/globalContext";
import {useEventEmitter} from "@hooks/useEventEmitter";
import { EEmitterEventName } from "@tools/CustomInterfaces";
import GlobalSearch from "@components/GlobalSearch";
import HeaderExtensions from "@extension/HeaderExtensions";
import themeConfig from "@resources/themeConfig.json"
import { checkContrast } from "@tools/colorContrastCheck";

const Navigator: React.FC = () => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const navigator = useNavigate();
    const { sideBarCollapsed, headerConfig } = useContext(GlobalContext);
    const [customItems, setCustomItems] = useState<{[key: string]: string}>({})
    const [styles, setStyles] = useState<any>()
    const {useListener } = useEventEmitter()
    const [searchParams] = useSearchParams();
    const dynamicChildName = searchParams.get('label');


    useListener(EEmitterEventName.layoutStyleChange , styles => {
        setStyles(styles.marginLeft ? {left: styles.marginLeft} : {})
    })

    useEffect(() => {
        let split = (pathname || "").split("/").filter(item => item);
        let title: string[] = [];

        split.forEach((item) => {
            title.push(customItems[item] || t(`menu:${item}.title`))
        });
        if(dynamicChildName)
            title.push(dynamicChildName)

        title = title.filter(i => i &&  i.indexOf(".") === -1);
        if (title.length)
            document.title = title.join(" - ")
    }, [pathname, t, customItems, dynamicChildName]);

    const routes = useMemo(() => {
        let pages = pathname.split("/")
        return pages.map((item, index) => {
            if (!item)
                return {path: "", breadcrumbName: ""};
            return  {
                path: pages.slice(0, index + 1).join("/"),
                breadcrumbName: customItems[item] || t(`menu:${item}.title`),
            }
        }).filter(i => i.path && i.breadcrumbName?.indexOf(".") === -1)
    }, [pathname, t, customItems])

    const onNavigatorItem = useCallback((data) => {
        setCustomItems(old => {
            Object.keys(data.data || {}).forEach(key => {
                old[key] = data.data?.[key]
            })
            return {...old}
        })
    }, [])

    useListener("navigatorItem" , onNavigatorItem)

    const items = useMemo(() => {
        let result = routes.map((item: any, index) => ({title: <Link key={index} to={item.path}>{item.breadcrumbName}</Link>}))
        if(dynamicChildName)
            result.push({title: <span>{dynamicChildName}</span>})
        return result
    }, [routes, dynamicChildName])

    const navigatorClass = useMemo(() => {
        if(sideBarCollapsed === "none") 
            return "noSideBar"
        else if(sideBarCollapsed)
            return "collapsed"
        return ""
    }, [sideBarCollapsed])

    const hasContrast = checkContrast(themeConfig.theme.primaryColor, "#111a2e")

    return <Layout.Header className={`mainBreadcrumb ${navigatorClass}`} style={{...styles, backgroundColor: themeConfig.theme.primaryColor}}>
        <Button type="text" shape="circle" icon={<ArrowLeftOutlined/>} onClick={() => navigator(-1)} style={{...!hasContrast && {color: "white"}}}/>
        <Breadcrumb style={{alignSelf: "center", flex: "0 0 auto", color: "white"}} className={`big${!hasContrast ? " negative" : ""}`} items={items}/>
        {(!headerConfig || headerConfig.showGlobalSearch )&& <GlobalSearch />}
        <SystemMessages />
        <HeaderExtensions />
        {(!headerConfig || headerConfig.showCurrencySwitch) && <CurrencySwitch />}
        {(!headerConfig || headerConfig.showCountrySwitch) && <LocaleSwitch />}
        <UserMenu negative={!hasContrast}/>
    </Layout.Header>
}

export default Navigator
